import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './StarFlip.css';

// Утилита для touch на Android WebView
const useTouchClick = (handler: () => void, disabled?: boolean) => {
  const touchedRef = useRef(false);
  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    touchedRef.current = true;
    handler();
    setTimeout(() => { touchedRef.current = false; }, 300);
  }, [disabled, handler]);
  const onClick = useCallback(() => {
    if (disabled || touchedRef.current) { touchedRef.current = false; return; }
    handler();
  }, [disabled, handler]);
  return { onTouchEnd, onClick };
};

interface SideConfig {
  name: string;
  value: string;
  chance: number;
  boost_duration: number;
  boost_percentage: number;
}

interface StarFlipProps {
  onPlay: (choice?: any) => void;
  onClose: () => void;
  cost: number;
  maxReward: number;
  minReward?: number;
  error?: string;
  isPlaying?: boolean;
  config?: SideConfig[];
  activeBoost?: {
    multiplier: number;
    timeLeft: number;
    expiresAt: string;
  } | null;
}

const StarFlip: React.FC<StarFlipProps> = ({ onPlay, onClose, cost, maxReward, error, isPlaying, activeBoost, config }) => {
  const { t } = useTranslation();
  const [selectedSide, setSelectedSide] = useState<'heads' | 'tails' | null>(null);

  // Получаем конфигурацию сторон из props или используем дефолтные значения
  const headsConfig = config?.find(s => s.value === 'heads') || {
    name: t('games.starFlip.moon'), value: 'heads', chance: 0.55, boost_duration: 180, boost_percentage: 30
  };
  const tailsConfig = config?.find(s => s.value === 'tails') || {
    name: t('games.starFlip.star'), value: 'tails', chance: 0.40, boost_duration: 120, boost_percentage: 100
  };

  const handlePlay = useCallback(() => {
    if (selectedSide) {
      console.log('🎮 StarFlip play!', selectedSide);
      onPlay(selectedSide);
    }
  }, [selectedSide, onPlay]);

  const handleSelectHeads = useCallback(() => {
    if (!isPlaying) setSelectedSide('heads');
  }, [isPlaying]);

  const handleSelectTails = useCallback(() => {
    if (!isPlaying) setSelectedSide('tails');
  }, [isPlaying]);

  const playBtnEvents = useTouchClick(handlePlay, !selectedSide || isPlaying);
  const headsBtnEvents = useTouchClick(handleSelectHeads, isPlaying);
  const tailsBtnEvents = useTouchClick(handleSelectTails, isPlaying);

  return (
    <div className="starflip-game">
      {/* Заголовок */}
      <div className="starflip-header">
        <div className="starflip-title">
          <span className="starflip-icon">🪙</span>
          <h2>{t('games.starFlip.title')}</h2>
        </div>
        <button className="starflip-close" onClick={onClose}>✕</button>
      </div>

      {/* Инструкция с описанием сторон */}
      <div className="starflip-instruction">
        <div className="starflip-instruction-title">{t('games.starFlip.chooseSide')}</div>
        <div className="starflip-sides-info">
          <div className="starflip-side-info moon">
            <span className="side-emoji">🌙</span>
            <span className="side-name">{t('games.starFlip.moon')}</span>
            <span className="side-chance">{t('games.starFlip.chance')} {Math.round(headsConfig.chance * 100)}%</span>
            <span className="side-boost">+{headsConfig.boost_percentage}% {t('games.starFlip.for')} {Math.round(headsConfig.boost_duration / 60)} {t('games.starFlip.min')}</span>
            <span className="side-desc">{t('games.starFlip.safeChoice')}</span>
          </div>
          <div className="starflip-vs">VS</div>
          <div className="starflip-side-info star">
            <span className="side-emoji">⭐</span>
            <span className="side-name">{t('games.starFlip.star')}</span>
            <span className="side-chance">{t('games.starFlip.chance')} {Math.round(tailsConfig.chance * 100)}%</span>
            <span className="side-boost">+{tailsConfig.boost_percentage}% {t('games.starFlip.for')} {Math.round(tailsConfig.boost_duration / 60)} {t('games.starFlip.min')}</span>
            <span className="side-desc">{t('games.starFlip.riskyChoice')}</span>
          </div>
        </div>
      </div>

      {/* Активный буст */}
      {activeBoost && activeBoost.timeLeft > 0 && (
        <div className="starflip-active-boost">
          <span className="boost-icon">⚡</span>
          <div className="boost-info">
            <div className="boost-title">{t('games.activeBoost')} x{activeBoost.multiplier}</div>
            <div className="boost-time">
              {t('games.remaining')}: {Math.floor(activeBoost.timeLeft / 60)}:{(activeBoost.timeLeft % 60).toString().padStart(2, '0')}
            </div>
          </div>
        </div>
      )}

      {/* Ошибка */}
      {error && (
        <div className="starflip-error">
          ❌ {error}
        </div>
      )}

      {/* Монетка */}
      <div className={`starflip-coin-area ${isPlaying ? 'flipping' : ''}`}>
        <div className="starflip-coin">
          <div className="coin-face front">🌙</div>
          <div className="coin-face back">⭐</div>
        </div>
      </div>

      {/* Кнопки выбора */}
      <div className="starflip-selection">
        <button
          className={`starflip-btn moon mobile-touch-btn ${selectedSide === 'heads' ? 'active' : ''}`}
          onTouchEnd={headsBtnEvents.onTouchEnd}
          onClick={headsBtnEvents.onClick}
          disabled={isPlaying}
          style={{ touchAction: 'manipulation' }}
        >
          <span className="btn-emoji">🌙</span>
          <span className="btn-label">{t('games.starFlip.moon')}</span>
          <span className="btn-stats">{Math.round(headsConfig.chance * 100)}% • +{headsConfig.boost_percentage}%</span>
        </button>

        <button
          className={`starflip-btn star mobile-touch-btn ${selectedSide === 'tails' ? 'active' : ''}`}
          onTouchEnd={tailsBtnEvents.onTouchEnd}
          onClick={tailsBtnEvents.onClick}
          disabled={isPlaying}
          style={{ touchAction: 'manipulation' }}
        >
          <span className="btn-emoji">⭐</span>
          <span className="btn-label">{t('games.starFlip.star')}</span>
          <span className="btn-stats">{Math.round(tailsConfig.chance * 100)}% • +{tailsConfig.boost_percentage}%</span>
        </button>
      </div>

      {/* Подсказка о выборе */}
      {selectedSide && (
        <div className={`starflip-choice-hint ${selectedSide === 'heads' ? 'moon' : 'star'}`}>
          {selectedSide === 'heads'
            ? t('games.starFlip.moonSelected')
            : t('games.starFlip.starSelected')}
        </div>
      )}

      {/* Кнопка играть */}
      <button
        className={`starflip-play mobile-touch-btn ${!selectedSide ? 'disabled' : ''} ${isPlaying ? 'playing' : ''}`}
        onTouchEnd={playBtnEvents.onTouchEnd}
        onClick={playBtnEvents.onClick}
        disabled={!selectedSide || isPlaying}
        style={{ touchAction: 'manipulation' }}
      >
        {isPlaying ? (
          <>
            <span className="play-spinner"></span>
            {t('games.starFlip.flipping')}
          </>
        ) : (
          <>
            🎲 {selectedSide ? t('games.starFlip.flipCoin') : t('games.starFlip.selectSide')}
          </>
        )}
      </button>

      {/* Стоимость */}
      <div className="starflip-cost">
        <span className="cost-label">{t('games.cost')}:</span>
        <span className="cost-value">{t('games.free')} ✨</span>
      </div>
    </div>
  );
};

export default StarFlip;
